import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api, EPGSourceSyncStatus } from '@/lib/api'
import { Plus, RefreshCw, Calendar, Trash2, X, Edit, CheckCircle, AlertCircle, Loader2, Clock } from 'lucide-react'

interface EPGSource {
  id: string
  name: string
  url: string
  type: string
  is_active: boolean
  sync_interval_hours: number
  sync_time?: string | null // "HH:MM" UTC for daily scheduled sync
  last_sync_at?: string
}

// Helper to format duration
function formatDuration(ms: number): string {
  if (ms < 1000) return `${ms}ms`
  const seconds = Math.floor(ms / 1000)
  if (seconds < 60) return `${seconds}s`
  const minutes = Math.floor(seconds / 60)
  const remainingSeconds = seconds % 60
  return `${minutes}m ${remainingSeconds}s`
}

// Helper to format relative time
function formatRelativeTime(dateStr: string): string {
  const date = new Date(dateStr)
  const now = new Date()
  const diffMs = date.getTime() - now.getTime()
  const diffMins = Math.round(diffMs / 60000)

  if (diffMins < 0) {
    const absMins = Math.abs(diffMins)
    if (absMins < 60) return `${absMins}m ago`
    const hours = Math.floor(absMins / 60)
    return `${hours}h ago`
  }
  if (diffMins < 60) return `in ${diffMins}m`
  const hours = Math.floor(diffMins / 60)
  return `in ${hours}h`
}

type SyncMode = 'interval' | 'time'

interface SourceFormData {
  name: string
  url: string
  syncMode: SyncMode
  syncIntervalHours: number
  syncTime: string
}

const defaultFormData: SourceFormData = {
  name: '',
  url: '',
  syncMode: 'interval',
  syncIntervalHours: 6,
  syncTime: '03:00',
}

export default function EPG() {
  const queryClient = useQueryClient()
  const [showAddModal, setShowAddModal] = useState(false)
  const [newSource, setNewSource] = useState<SourceFormData>({ ...defaultFormData })
  const [addError, setAddError] = useState('')
  const [editingSource, setEditingSource] = useState<EPGSource | null>(null)
  const [editData, setEditData] = useState<SourceFormData>({ ...defaultFormData })
  const [editError, setEditError] = useState('')

  const { data: sources, isLoading } = useQuery({
    queryKey: ['epg-sources'],
    queryFn: () => api.getEPGSources(),
  })

  // Sync status with polling - polls every 2 seconds when syncing, otherwise every 10 seconds
  const { data: syncStatus } = useQuery({
    queryKey: ['epg-sync-status'],
    queryFn: () => api.getEPGSyncStatus(),
    refetchInterval: (query) => {
      const data = query.state.data
      const hasSyncing = data?.sources?.some((s: EPGSourceSyncStatus) => s.status === 'syncing')
      return hasSyncing ? 2000 : 10000
    },
  })

  // Create a map for quick lookup of sync status by source ID
  const syncStatusMap = new Map<string, EPGSourceSyncStatus>()
  syncStatus?.sources?.forEach((s: EPGSourceSyncStatus) => {
    syncStatusMap.set(s.source_id, s)
  })

  const syncMutation = useMutation({
    mutationFn: (id: string) => api.syncEPGSource(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['epg-sources'] })
    },
  })

  const addMutation = useMutation({
    mutationFn: (data: SourceFormData) => {
      const payload = {
        name: data.name,
        url: data.url,
        sync_interval_hours: data.syncMode === 'interval' ? data.syncIntervalHours : 0,
        sync_time: data.syncMode === 'time' ? data.syncTime : null,
      }
      return api.createEPGSource(payload)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['epg-sources'] })
      setShowAddModal(false)
      setNewSource({ ...defaultFormData })
      setAddError('')
    },
    onError: (err: Error) => {
      setAddError(err.message || 'Failed to add EPG source')
    },
  })

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.deleteEPGSource(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['epg-sources'] })
    },
  })

  const updateMutation = useMutation({
    mutationFn: ({ id, data, source }: { id: string; data: SourceFormData; source: EPGSource }) => {
      const payload = {
        name: data.name,
        url: data.url,
        type: source.type || 'xmltv',
        is_active: source.is_active,
        sync_interval_hours: data.syncMode === 'interval' ? data.syncIntervalHours : 0,
        sync_time: data.syncMode === 'time' ? data.syncTime : '',
      }
      return api.updateEPGSource(id, payload)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['epg-sources'] })
      setEditingSource(null)
      setEditData({ ...defaultFormData })
      setEditError('')
    },
    onError: (err: Error) => {
      setEditError(err.message || 'Failed to update EPG source')
    },
  })

  const handleAddSource = (e: React.FormEvent) => {
    e.preventDefault()
    setAddError('')
    if (!newSource.name.trim() || !newSource.url.trim()) {
      setAddError('Name and URL are required')
      return
    }
    if (newSource.syncMode === 'interval' && (newSource.syncIntervalHours < 1 || newSource.syncIntervalHours > 168)) {
      setAddError('Sync interval must be between 1 and 168 hours')
      return
    }
    if (newSource.syncMode === 'time' && !/^([01][0-9]|2[0-3]):[0-5][0-9]$/.test(newSource.syncTime)) {
      setAddError('Sync time must be in HH:MM format (00:00 - 23:59)')
      return
    }
    addMutation.mutate(newSource)
  }

  const handleEditSource = (e: React.FormEvent) => {
    e.preventDefault()
    setEditError('')
    if (!editData.name.trim() || !editData.url.trim()) {
      setEditError('Name and URL are required')
      return
    }
    if (editData.syncMode === 'interval' && (editData.syncIntervalHours < 1 || editData.syncIntervalHours > 168)) {
      setEditError('Sync interval must be between 1 and 168 hours')
      return
    }
    if (editData.syncMode === 'time' && !/^([01][0-9]|2[0-3]):[0-5][0-9]$/.test(editData.syncTime)) {
      setEditError('Sync time must be in HH:MM format (00:00 - 23:59)')
      return
    }
    if (editingSource) {
      updateMutation.mutate({
        id: editingSource.id,
        data: editData,
        source: editingSource,
      })
    }
  }

  const openEditModal = (source: EPGSource) => {
    setEditingSource(source)
    const hasTimeSync = source.sync_time && source.sync_time !== ''
    setEditData({
      name: source.name,
      url: source.url,
      syncMode: hasTimeSync ? 'time' : 'interval',
      syncIntervalHours: source.sync_interval_hours || 6,
      syncTime: source.sync_time || '03:00',
    })
    setEditError('')
  }

  return (
    <div className="space-y-8">
      <div className="flex items-center justify-between gap-4">
        <h1 className="text-2xl sm:text-3xl font-semibold text-foreground">
          EPG Sources
        </h1>
        <button className="btn-primary shrink-0" onClick={() => setShowAddModal(true)}>
          <Plus className="w-5 h-5" />
          <span className="hidden sm:inline">Add Source</span>
          <span className="sm:hidden">Add</span>
        </button>
      </div>

      {/* Add Source Modal */}
      {showAddModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-card border border-border rounded-lg shadow-lg w-full max-w-md p-4 sm:p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg sm:text-xl font-semibold text-foreground">Add EPG Source</h2>
              <button
                onClick={() => {
                  setShowAddModal(false)
                  setNewSource({ ...defaultFormData })
                  setAddError('')
                }}
                className="p-2 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleAddSource} className="space-y-4">
              {addError && (
                <div className="bg-destructive/10 border border-destructive/20 text-destructive px-4 py-3 rounded-lg text-sm">
                  {addError}
                </div>
              )}

              <div>
                <label className="form-label">Name</label>
                <input
                  type="text"
                  value={newSource.name}
                  onChange={(e) => setNewSource({ ...newSource, name: e.target.value })}
                  className="input-field"
                  placeholder="My EPG Source"
                />
              </div>

              <div>
                <label className="form-label">URL</label>
                <input
                  type="url"
                  value={newSource.url}
                  onChange={(e) => setNewSource({ ...newSource, url: e.target.value })}
                  className="input-field"
                  placeholder="http://example.com/epg.xml"
                />
                <p className="text-sm text-muted-foreground mt-1">
                  XMLTV format URL (supports .xml and .gz)
                </p>
              </div>

              <div>
                <label className="form-label">Sync Schedule</label>
                <div className="space-y-3 mt-2">
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="radio"
                      name="syncMode"
                      checked={newSource.syncMode === 'interval'}
                      onChange={() => setNewSource({ ...newSource, syncMode: 'interval' })}
                      className="w-4 h-4 text-primary"
                    />
                    <span className="text-sm text-foreground">Every</span>
                    <input
                      type="number"
                      min="1"
                      max="168"
                      value={newSource.syncIntervalHours}
                      onChange={(e) => setNewSource({ ...newSource, syncIntervalHours: parseInt(e.target.value) || 6 })}
                      disabled={newSource.syncMode !== 'interval'}
                      className="input-field w-20 disabled:opacity-50"
                    />
                    <span className="text-sm text-foreground">hours</span>
                  </label>
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="radio"
                      name="syncMode"
                      checked={newSource.syncMode === 'time'}
                      onChange={() => setNewSource({ ...newSource, syncMode: 'time' })}
                      className="w-4 h-4 text-primary"
                    />
                    <span className="text-sm text-foreground">Daily at</span>
                    <input
                      type="time"
                      value={newSource.syncTime}
                      onChange={(e) => setNewSource({ ...newSource, syncTime: e.target.value })}
                      disabled={newSource.syncMode !== 'time'}
                      className="input-field w-28 disabled:opacity-50"
                    />
                    <span className="text-sm text-foreground">UTC</span>
                  </label>
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowAddModal(false)
                    setNewSource({ ...defaultFormData })
                    setAddError('')
                  }}
                  className="btn-secondary"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={addMutation.isPending}
                  className="btn-primary"
                >
                  {addMutation.isPending ? 'Adding...' : 'Add Source'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Source Modal */}
      {editingSource && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-card border border-border rounded-lg shadow-lg w-full max-w-md p-4 sm:p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg sm:text-xl font-semibold text-foreground">Edit EPG Source</h2>
              <button
                onClick={() => {
                  setEditingSource(null)
                  setEditData({ ...defaultFormData })
                  setEditError('')
                }}
                className="p-2 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleEditSource} className="space-y-4">
              {editError && (
                <div className="bg-destructive/10 border border-destructive/20 text-destructive px-4 py-3 rounded-lg text-sm">
                  {editError}
                </div>
              )}

              <div>
                <label className="form-label">Name</label>
                <input
                  type="text"
                  value={editData.name}
                  onChange={(e) => setEditData({ ...editData, name: e.target.value })}
                  className="input-field"
                  placeholder="My EPG Source"
                />
              </div>

              <div>
                <label className="form-label">URL</label>
                <input
                  type="url"
                  value={editData.url}
                  onChange={(e) => setEditData({ ...editData, url: e.target.value })}
                  className="input-field"
                  placeholder="http://example.com/epg.xml"
                />
                <p className="text-sm text-muted-foreground mt-1">
                  XMLTV format URL (supports .xml and .gz)
                </p>
              </div>

              <div>
                <label className="form-label">Sync Schedule</label>
                <div className="space-y-3 mt-2">
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="radio"
                      name="editSyncMode"
                      checked={editData.syncMode === 'interval'}
                      onChange={() => setEditData({ ...editData, syncMode: 'interval' })}
                      className="w-4 h-4 text-primary"
                    />
                    <span className="text-sm text-foreground">Every</span>
                    <input
                      type="number"
                      min="1"
                      max="168"
                      value={editData.syncIntervalHours}
                      onChange={(e) => setEditData({ ...editData, syncIntervalHours: parseInt(e.target.value) || 6 })}
                      disabled={editData.syncMode !== 'interval'}
                      className="input-field w-20 disabled:opacity-50"
                    />
                    <span className="text-sm text-foreground">hours</span>
                  </label>
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="radio"
                      name="editSyncMode"
                      checked={editData.syncMode === 'time'}
                      onChange={() => setEditData({ ...editData, syncMode: 'time' })}
                      className="w-4 h-4 text-primary"
                    />
                    <span className="text-sm text-foreground">Daily at</span>
                    <input
                      type="time"
                      value={editData.syncTime}
                      onChange={(e) => setEditData({ ...editData, syncTime: e.target.value })}
                      disabled={editData.syncMode !== 'time'}
                      className="input-field w-28 disabled:opacity-50"
                    />
                    <span className="text-sm text-foreground">UTC</span>
                  </label>
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setEditingSource(null)
                    setEditData({ ...defaultFormData })
                    setEditError('')
                  }}
                  className="btn-secondary"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={updateMutation.isPending}
                  className="btn-primary"
                >
                  {updateMutation.isPending ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Info */}
      <div className="bg-primary/5 border border-primary/20 rounded-lg p-4">
        <div className="flex gap-4">
          <Calendar className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
          <div className="text-base">
            <p className="font-medium text-foreground">Electronic Program Guide (EPG)</p>
            <p className="mt-1 text-muted-foreground">
              EPG data is synchronized automatically. You can also trigger manual sync for each source.
            </p>
          </div>
        </div>
      </div>

      {/* Sources - Card layout on mobile, table on desktop */}
      {isLoading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      ) : sources?.length ? (
        <div className="space-y-4">
          {sources.map((source: EPGSource) => {
            const status = syncStatusMap.get(source.id)
            const isSyncing = status?.status === 'syncing'
            const hasError = status?.status === 'error'
            const isCompleted = status?.status === 'completed'

            return (
              <div key={source.id} className="bg-card rounded-lg border border-border p-4 sm:p-6">
                {/* Header row: name + actions */}
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="text-base font-medium text-foreground">
                      {source.name}
                    </div>
                    <div className="text-xs text-muted-foreground mt-0.5">
                      {source.type || 'XMLTV'}
                    </div>
                    <div className="text-xs text-muted-foreground mt-1 truncate">
                      {source.url}
                    </div>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <button
                      onClick={() => openEditModal(source)}
                      className="p-2 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                      title="Edit"
                    >
                      <Edit className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => {
                        syncMutation.mutate(source.id)
                        queryClient.invalidateQueries({ queryKey: ['epg-sync-status'] })
                      }}
                      disabled={isSyncing}
                      className="p-2 rounded-lg text-muted-foreground hover:text-primary hover:bg-primary/10 transition-colors disabled:opacity-50"
                      title={isSyncing ? 'Sync in progress' : 'Sync now'}
                    >
                      <RefreshCw className={`w-4 h-4 ${isSyncing ? 'animate-spin' : ''}`} />
                    </button>
                    <button
                      onClick={() => {
                        if (confirm('Are you sure you want to delete this EPG source?')) {
                          deleteMutation.mutate(source.id)
                        }
                      }}
                      disabled={deleteMutation.isPending || isSyncing}
                      className="p-2 rounded-lg text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors disabled:opacity-50"
                      title="Delete"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                {/* Status + Stats row */}
                <div className="mt-3 pt-3 border-t border-border grid grid-cols-1 sm:grid-cols-3 gap-3">
                  {/* Sync Status */}
                  <div className="space-y-1">
                    <div className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Status</div>
                    <div className="flex items-center gap-2">
                      {isSyncing ? (
                        <>
                          <Loader2 className="w-4 h-4 text-primary animate-spin" />
                          <span className="text-sm font-medium text-primary">Syncing...</span>
                        </>
                      ) : hasError ? (
                        <>
                          <AlertCircle className="w-4 h-4 text-destructive" />
                          <span className="text-sm font-medium text-destructive">Error</span>
                        </>
                      ) : isCompleted ? (
                        <>
                          <CheckCircle className="w-4 h-4 text-green-500" />
                          <span className="text-sm font-medium text-green-500">Synced</span>
                        </>
                      ) : (
                        <>
                          <Clock className="w-4 h-4 text-muted-foreground" />
                          <span className="text-sm text-muted-foreground">Pending</span>
                        </>
                      )}
                    </div>
                    {hasError && status?.error && (
                      <div className="text-xs text-destructive truncate" title={status.error}>
                        {status.error}
                      </div>
                    )}
                  </div>

                  {/* Schedule */}
                  <div className="space-y-1">
                    <div className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Schedule</div>
                    <div className="text-xs text-muted-foreground space-y-0.5">
                      {status?.sync_time ? (
                        <div className="text-primary/80">Daily at {status.sync_time} UTC</div>
                      ) : status?.sync_interval_hours ? (
                        <div className="text-primary/80">Every {status.sync_interval_hours}h</div>
                      ) : source.sync_time ? (
                        <div className="text-primary/80">Daily at {source.sync_time} UTC</div>
                      ) : source.sync_interval_hours ? (
                        <div className="text-primary/80">Every {source.sync_interval_hours}h</div>
                      ) : null}
                      {status?.last_sync && (
                        <div>Last: {formatRelativeTime(status.last_sync)}</div>
                      )}
                      {status?.next_sync && !isSyncing && (
                        <div>Next: {formatRelativeTime(status.next_sync)}</div>
                      )}
                      {isSyncing && status?.duration_ms > 0 && (
                        <div>Running: {formatDuration(status.duration_ms)}</div>
                      )}
                    </div>
                  </div>

                  {/* Stats */}
                  <div className="space-y-1">
                    <div className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Stats</div>
                    <div className="text-sm">
                      {status && (status.channel_count > 0 || status.program_count > 0) ? (
                        <div className="flex flex-wrap gap-x-4 gap-y-0.5">
                          <span className="text-muted-foreground">
                            Channels: <span className="font-medium text-foreground">{status.channel_count.toLocaleString()}</span>
                          </span>
                          <span className="text-muted-foreground">
                            Programs: <span className="font-medium text-foreground">{status.program_count.toLocaleString()}</span>
                            {isSyncing && <span className="text-primary ml-1">...</span>}
                          </span>
                          {isCompleted && status.duration_ms > 0 && (
                            <span className="text-muted-foreground">
                              Duration: <span className="font-medium text-foreground">{formatDuration(status.duration_ms)}</span>
                            </span>
                          )}
                        </div>
                      ) : (
                        <span className="text-muted-foreground">No data yet</span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      ) : (
        <div className="bg-card rounded-lg border border-border px-6 py-12 text-center text-base text-muted-foreground">
          No EPG sources configured
        </div>
      )}
    </div>
  )
}
